import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, ServiceResource, TechnicianWorkQueueEntry } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-technician-work-queue',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, TableModule, ButtonModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.technician.title')" [description]="t('catalog.technician.description')">
      @if (loadingResources()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="selector">
          <label>
            <span>{{ t('catalog.service.resource') }}</span>
            <p-select [options]="resourceOptions()" [(ngModel)]="selectedResourceId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" (ngModelChange)="loadQueue()" />
          </label>
          <a [routerLink]="['/operations/control-room']">{{ t('catalog.action.backToList') }}</a>
        </section>

        @if (loadingQueue()) {
          <ain-state type="loading" [title]="t('catalog.loading')" />
        } @else {
          <section class="queue">
            @for (item of rows(); track item.orderId) {
              <article [class.breach]="item.isSlaBreached">
                <header>
                  <strong>{{ item.orderNumber }}</strong>
                  <span>{{ item.status }}</span>
                </header>
                <p>{{ item.title }}</p>
                <p class="meta">{{ item.customerReferenceNumber }} · {{ item.scheduledStartUtc || t('catalog.technician.unscheduled') }}</p>
                <div class="actions">
                  @if (item.status === 'Assigned') {
                    <button pButton type="button" [label]="t('catalog.service.action.start')" (click)="startOrder(item.orderId)"></button>
                  }
                  <button pButton type="button" severity="secondary" [label]="t('catalog.technician.openOrder')" (click)="openOrder(item.orderId)"></button>
                </div>
              </article>
            } @empty {
              <p class="empty">{{ t('catalog.technician.emptyQueue') }}</p>
            }
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .selector { display: flex; gap: 12px; align-items: end; flex-wrap: wrap; margin-bottom: 14px; }
    .selector label { display: grid; gap: 6px; min-width: 240px; }
    .selector span { font-size: 12px; color: #475569; font-weight: 600; }
    .selector a { color: #1d4ed8; font-weight: 600; text-decoration: none; margin-bottom: 8px; }
    .queue { display: grid; gap: 10px; }
    .queue article { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
    .queue article.breach { border-color: #fca5a5; background: #fef2f2; }
    .queue header { display: flex; justify-content: space-between; gap: 8px; }
    .queue p { margin: 8px 0 0; color: #334155; }
    .queue .meta { font-size: 12px; color: #64748b; }
    .queue .actions { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
    .empty { color: #64748b; }
  `]
})
export class TechnicianWorkQueueComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loadingResources = signal(true);
  readonly loadingQueue = signal(false);
  readonly resources = signal<ServiceResource[]>([]);
  readonly rows = signal<TechnicianWorkQueueEntry[]>([]);

  selectedResourceId = '';

  readonly resourceOptions = computed(() =>
    this.resources().map(r => ({ label: `${r.code} - ${r.name}`, value: r.id }))
  );

  ngOnInit(): void {
    this.service.listServiceResources().subscribe({
      next: resources => {
        this.resources.set(resources.filter(r => r.isActive));
        if (resources.length > 0) {
          this.selectedResourceId = resources[0].id;
          this.loadQueue();
        }
        this.loadingResources.set(false);
      },
      error: () => this.loadingResources.set(false)
    });
  }

  loadQueue(): void {
    if (!this.selectedResourceId) {
      this.rows.set([]);
      return;
    }

    this.loadingQueue.set(true);
    this.service.getTechnicianWorkQueue(this.selectedResourceId).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loadingQueue.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loadingQueue.set(false);
      }
    });
  }

  openOrder(id: string): void {
    void this.router.navigate(['/services/orders', id]);
  }

  startOrder(id: string): void {
    this.service.startServiceOrder(id).subscribe({
      next: () => this.loadQueue(),
      error: () => this.loadQueue()
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
